import { useState } from 'react';

import { isAxiosError } from 'axios';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { Result } from 'neverthrow';

import { Button } from 'components/Button';
import { Textarea } from 'components/Textarea';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import { cn } from 'utils/shadcn';

export enum KitFeedbackRating {
  Up = 'up',
  Down = 'down',
}

type KitFeedbackProps = {
  upLabel: string;
  downLabel: string;
  upPlaceholder: string;
  downPlaceholder: string;
  buttonNamePosthog: string;
  onSubmit: (
    rating: KitFeedbackRating,
    note: string
  ) => Promise<Result<unknown, ApiError>>;
  question?: string;
  // Toast after a successful send; the inline thanks shows either way.
  successToast?: string;
  // The rep already rated this (e.g. before leaving and reopening the thread).
  isAlreadySubmitted?: boolean;
  testId?: string;
};

const NOTE_MAX_LENGTH = 500;

// Thumbs up/down on something Kit did, with an optional short note.
export function KitFeedback({
  upLabel,
  downLabel,
  upPlaceholder,
  downPlaceholder,
  buttonNamePosthog,
  onSubmit,
  question = 'Did Kit get this right?',
  successToast,
  isAlreadySubmitted = false,
  testId = 'kit-feedback',
}: KitFeedbackProps) {
  const { toast } = useToast();
  const [rating, setRating] = useState<Maybe<KitFeedbackRating>>(null);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(isAlreadySubmitted);

  const handleSubmit = async () => {
    if (!rating) {
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await onSubmit(rating, note);

      if (result.isErr()) {
        toast({ description: result.error.message, variant: 'destructive' });
        return;
      }

      setIsSubmitted(true);
      if (successToast) {
        toast({ description: successToast, variant: 'success' });
      }
    } catch (error) {
      // Re-throw auth errors so the app can handle them (e.g. redirect to re-auth)
      if (isAxiosError(error) && error.response?.status === 401) {
        throw error;
      }
      toast({
        description: "Couldn't save your feedback. Try again.",
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Typography
        variant='body-xs'
        textColor='muted'
        className='text-[11px]'
        data-testid={testId}
      >
        Thanks for the feedback.
      </Typography>
    );
  }

  return (
    <Flex direction='col' gap='xs' className='w-full' data-testid={testId}>
      <Flex align='center' justify='between' className='w-full'>
        <Typography
          as='span'
          variant='body-xs'
          textColor='muted'
          className='text-[11px]'
        >
          {question}
        </Typography>
        <Flex align='center' gap='xs'>
          <RatingButton
            value={KitFeedbackRating.Up}
            label={upLabel}
            Icon={ThumbsUp}
            selected={rating}
            onSelect={setRating}
          />
          <RatingButton
            value={KitFeedbackRating.Down}
            label={downLabel}
            Icon={ThumbsDown}
            selected={rating}
            onSelect={setRating}
          />
        </Flex>
      </Flex>

      {rating && (
        <Flex direction='col' gap='xs' className='w-full'>
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={NOTE_MAX_LENGTH}
            placeholder={
              rating === KitFeedbackRating.Down
                ? downPlaceholder
                : upPlaceholder
            }
            aria-label='Feedback note'
            className='min-h-[52px] text-[12px]'
          />
          <Button
            type='button'
            size='sm'
            className='h-7 w-fit self-end px-3 text-[12px]'
            buttonNamePosthog={buttonNamePosthog}
            logProperties={{ rating }}
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting && <ButtonLoader className='h-3.5 w-3.5' />}
            Send feedback
          </Button>
        </Flex>
      )}
    </Flex>
  );
}

type RatingButtonProps = {
  value: KitFeedbackRating;
  label: string;
  Icon: typeof ThumbsUp;
  selected: Maybe<KitFeedbackRating>;
  onSelect: (rating: Maybe<KitFeedbackRating>) => void;
};

function RatingButton({
  value,
  label,
  Icon,
  selected,
  onSelect,
}: RatingButtonProps) {
  const isSelected = selected === value;

  return (
    <button
      type='button'
      aria-label={label}
      aria-pressed={isSelected}
      onClick={() => onSelect(isSelected ? null : value)}
      className={cn(
        'flex h-6 w-6 cursor-pointer items-center justify-center rounded-[4px] border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
        isSelected
          ? 'border-brand-main bg-brand-50 text-brand-main'
          : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-800'
      )}
    >
      <Icon className='h-3.5 w-3.5' aria-hidden />
    </button>
  );
}
